import { exchangeShortLivedToken } from '@lib/network/tokenExchange';
import { readAndClearPostSignupRedirectCookie } from '@lib/utils/onboardingRedirect';
import {
  isAllowedExternalRedirect,
  parseAllowedRedirectPatterns,
} from '@lib/utils/redirectAllowlist';
import {
  isPendingRedirectExpired,
  parsePendingRedirect,
  serializePendingRedirect,
} from '@lib/utils/redirectStash';
import { parseSearchParam } from '@lib/utils/url';
import { router } from '@pages/App/router';
import { reaction } from 'mobx';
import { trackingStore } from './Tracking';
import { userStore } from './User';

/**
 * Single owner of every post-auth redirect concern: the durable `?redirect=`
 * stash, allowlist validation, the verified-session gate, the short-lived
 * token exchange, the actual navigation, and the post-onboarding fallback
 * destination.
 *
 * History: this logic used to be spread across UserStore (a `tokenIsValid`
 * reaction + redirectIfRequested), the WithUser effect (billing-gated), and
 * WithoutUser/DeferredNavigation — four competing navigators whose races
 * intermittently stranded users in /workspace instead of bouncing them back
 * to the external app they came from (see the 5+ prior "lost redirect" fixes
 * in git history). Centralizing gives one deterministic driver:
 *
 *   - The `?redirect=` target is captured once per full page load (here, at
 *     construction) into sessionStorage with a timestamp, surviving both
 *     competing SPA navigations and mid-flow full reloads (session-expiry
 *     bounce, bootstrap-error reload) for up to the stash TTL.
 *   - A single MobX reaction (bound in initCrossStoreReactions) fires the
 *     redirect the moment the session is confirmed verified — independent of
 *     which components happen to be mounted, and with no billing dependency
 *     (an earlier billing gate silently stranded the redirect whenever the
 *     billing fetch errored; the paywall deliberately sits after this, so a
 *     bounce to another app is never held up by this app's wall).
 *   - External targets get a short-lived exchange token instead of the
 *     long-lived session JWT, falling back to the JWT only if the exchange
 *     fails, so the user is never stranded.
 *
 * Cycle rule: this module sits in the same import cycle as User ↔ router ↔
 * pages. The constructor must only touch `window.location` / `sessionStorage`
 * — never dereference `userStore` or `router` (their bindings may still be in
 * their temporal dead zone at construction). Reactions bind in
 * initCrossStoreReactions(); navigation happens at call time.
 */

// Durable copy of the `?redirect=` target captured at page load. Same key the
// previous implementation used, so an in-flight login across the deploy
// boundary keeps its target (redirectStash parses the legacy bare-string
// format).
const PENDING_REDIRECT_KEY = 'pendingExternalRedirect';

// User-settings key holding a destination chosen on the marketing landing
// (carried in via a `.starzero.ai` cookie, see lib/utils/onboardingRedirect).
// Persisted server-side so it survives the magic-link round-trip and reloads;
// honored once post-auth when no explicit redirect takes precedence.
const POST_ONBOARDING_REDIRECT_KEY = 'postOnboardingRedirect';

const ALLOWED_EXTERNAL_REDIRECT_PATTERNS = parseAllowedRedirectPatterns(
  process.env.ALLOWED_EXTERNAL_REDIRECT ?? '',
);

type PendingTarget = { url: string; kind: 'external' | 'internal' };

function isExternalUrl(url: string): boolean {
  return url.includes('://') || url.startsWith('//');
}

export class PostAuthRedirectStore {
  private crossStoreReactionsBound = false;

  /**
   * Single-flight guard: the reaction driver and the WithUser effect can both
   * invoke redirectIfRequested around the same state transition; concurrent
   * callers share one attempt. Reset for non-external outcomes; kept after an
   * external navigation (the page is unloading — a re-fire on a stale target
   * is exactly what we don't want).
   */
  private redirectInFlight: Promise<boolean> | null = null;

  /**
   * A blocked target that came from the live URL param can't be "cleared" the
   * way the stash can, and the caller effect re-runs on unrelated state
   * changes — dedupe the Sentry-visible error log per target.
   */
  private lastBlockedLogged: string | null = null;

  private postOnboardingRedirectInFlight = false;

  constructor() {
    // Capture any `?redirect=` from the entry URL durably, BEFORE any in-app
    // navigation can strip it. Full page loads (the chat→app bounce and the
    // magic-link email click) re-capture and refresh the timestamp. A load
    // WITHOUT the param leaves an existing stash alone — wiping it here is
    // what used to kill the redirect on any mid-flow reload; staleness is
    // bounded by the stash TTL instead.
    const entryRedirect = parseSearchParam(window.location.search).get('redirect')?.trim();
    if (entryRedirect) {
      sessionStorage.setItem(
        PENDING_REDIRECT_KEY,
        serializePendingRedirect(entryRedirect, Date.now()),
      );
    }
  }

  /**
   * Bind the single redirect driver. Fires on every false→true transition of
   * "session confirmed and verified": page-load /user/me resolve (the
   * already-logged-in bounce), password login, magic-link/email-code
   * consumption, email verification completing, and the service-worker-driven
   * refetch after a cross-tab verify. Re-arms when the session drops (data →
   * null on expiry/logout) and the user logs back in.
   */
  initCrossStoreReactions() {
    if (this.crossStoreReactionsBound) return;
    this.crossStoreReactionsBound = true;
    reaction(
      () => !!userStore.data?.isVerified,
      (isVerified) => {
        if (isVerified) {
          void this.redirectIfRequested();
        }
      },
      { fireImmediately: true },
    );
  }

  /** True when an allowed EXTERNAL redirect target is pending. */
  hasPendingExternalRedirect(): boolean {
    return this.resolvePendingTarget()?.kind === 'external';
  }

  /**
   * True when any `?redirect=` target (allowed external OR internal) is
   * pending. Callers (the WithUser post-auth effect) use this to avoid
   * preempting redirectIfRequested with their own navigation. Blocked and
   * expired targets are cleared + logged during resolution and return false,
   * so normal post-auth navigation proceeds for them.
   */
  hasPendingRedirect(): boolean {
    return this.resolvePendingTarget() !== null;
  }

  /**
   * Perform the pending redirect, if any. Returns true when a navigation was
   * initiated. External targets require a verified session; until then the
   * stash is kept and the call is a no-op (the driver reaction re-fires once
   * verified).
   */
  redirectIfRequested = (): Promise<boolean> => {
    if (!this.redirectInFlight) {
      this.redirectInFlight = this.performRedirect()
        .then((outcome) => {
          if (outcome !== 'external') {
            this.redirectInFlight = null;
          }
          return outcome !== 'none';
        })
        .catch((error) => {
          this.redirectInFlight = null;
          console.error('[postAuthRedirect] redirect attempt failed:', error);
          return false;
        });
    }
    return this.redirectInFlight;
  };

  private clearPendingRedirect() {
    sessionStorage.removeItem(PENDING_REDIRECT_KEY);
  }

  /**
   * The requested redirect target: live URL param first (freshest), then the
   * durable stash. Expired or allowlist-blocked targets are consumed and
   * logged here so they can't linger or re-fire.
   */
  private resolvePendingTarget(): PendingTarget | null {
    let url = parseSearchParam(window.location.search).get('redirect')?.trim() || null;
    if (!url) {
      const stash = parsePendingRedirect(sessionStorage.getItem(PENDING_REDIRECT_KEY));
      if (stash) {
        if (isPendingRedirectExpired(stash, Date.now())) {
          this.clearPendingRedirect();
          console.error('[postAuthRedirect] pending redirect expired unconsumed:', stash.url);
        } else {
          url = stash.url;
        }
      }
    }
    if (!url) {
      return null;
    }
    if (!isExternalUrl(url)) {
      return { url, kind: 'internal' };
    }
    if (!isAllowedExternalRedirect(url, ALLOWED_EXTERNAL_REDIRECT_PATTERNS)) {
      this.clearPendingRedirect();
      if (this.lastBlockedLogged !== url) {
        this.lastBlockedLogged = url;
        console.error('[postAuthRedirect] blocked external redirect (not in allowlist):', url);
      }
      return null;
    }
    return { url, kind: 'external' };
  }

  private async performRedirect(): Promise<'external' | 'internal' | 'none'> {
    const target = this.resolvePendingTarget();
    if (!target) {
      return 'none';
    }

    if (target.kind === 'internal') {
      // Backslashes normalize to slashes during URL resolution, which could
      // turn a "path" protocol-relative — reject outright.
      if (target.url.includes('\\')) {
        this.clearPendingRedirect();
        console.error('[postAuthRedirect] blocked malformed internal redirect:', target.url);
        return 'none';
      }
      this.clearPendingRedirect();
      router.navigate(target.url);
      return 'internal';
    }

    // External targets wait for a confirmed, verified session. Keep the stash
    // and bail; the driver reaction re-fires when verification lands. (No
    // billing dependency — leaving the app doesn't need billing data.)
    if (!userStore.data?.isVerified) {
      return 'none';
    }

    // NB: the stash is consumed inside navigateExternalWithToken, immediately
    // before the navigation is committed — not here. Clearing it up front lost
    // the target whenever the token exchange failed to produce one (see there).
    const navigated = await this.navigateExternalWithToken(target.url, 'redirect-param');
    return navigated ? 'external' : 'none';
  }

  /**
   * Navigate to an allowlist-approved external URL with an auth token under
   * `?token=`. Prefers a short-lived exchange token (the long-lived session
   * JWT then never lands in URLs / history / logs); falls back to the session
   * JWT if the exchange fails so the user is never stranded in the app.
   */
  private async navigateExternalWithToken(
    url: string,
    control: 'redirect-param' | 'post-onboarding-cookie',
  ): Promise<boolean> {
    // Label the handoff so it isn't just unexplained idle time in Mixpanel.
    // Tracked from the tokenless `url`, never the tokened one. `surface: 'auth'`
    // marks this as a redirect rather than a deliberate click — most of these are
    // the chat→app→chat login bounce, so they're users returning to where they
    // started, not leaving.
    //
    // Fired BEFORE the exchange (the same rationale as openAuthedChatUrl in
    // chats.ts): a handoff the user initiated is recorded even when the exchange
    // fails and the no-token path below bails without navigating.
    //
    // The allowlist also permits non-agents hosts (starzero.ai, api.starzero.ai,
    // app.vibedit.ai); those still fire, tagged `isAgentsApp: false` with the
    // real `targetHost`.
    trackingStore.agentsAppOpened({
      url,
      requestedNewTab: false,
      surface: 'auth',
      control,
    });

    let token: string | null;
    try {
      token = await exchangeShortLivedToken();
    } catch (error) {
      console.error(
        '[postAuthRedirect] short-lived token exchange failed; falling back to the session token:',
        error,
      );
      token = userStore.token;
    }
    if (!token) {
      // Reachable: the exchange call goes through apiWithAuth, which on a 401 or a
      // failed refresh runs handleSessionExpired() — nulling both the token and the
      // session and bouncing to /login. The fallback then reads a null token and we
      // land here. The stash is deliberately still intact at this point, so the
      // driver reaction re-fires the redirect once the user logs back in (within the
      // stash TTL). Clearing it before the exchange, as this used to, stranded the
      // user in the app on exactly the path this store exists to fix.
      console.error('[postAuthRedirect] no token available for external redirect:', url);
      return false;
    }
    const externalURL = new URL(url);
    externalURL.searchParams.set('token', token);
    // Consume the stash only now that the navigation is certain: sessionStorage
    // survives the same-tab navigation to the external app, so leaving it set
    // could re-trigger a stale redirect if the user comes back.
    this.clearPendingRedirect();
    window.location.href = externalURL.toString();
    return true;
  }

  /**
   * A destination remembered from the marketing landing (cookie -> user
   * settings), to send the user to once post-auth. Null when none is pending.
   */
  get pendingPostOnboardingRedirect(): string | null {
    const dest = userStore.data?.settings?.[POST_ONBOARDING_REDIRECT_KEY];
    return typeof dest === 'string' && dest.trim() ? dest.trim() : null;
  }

  /**
   * If the landing dropped a `.starzero.ai` redirect cookie, persist it to user
   * settings (durable + browser-independent from here) and consume the cookie.
   * No-op unless authenticated and nothing is already pending. Safe to call on
   * every authenticated load — the cookie is cleared after the first read.
   */
  capturePostOnboardingRedirect = async (): Promise<void> => {
    const fromCookie = readAndClearPostSignupRedirectCookie();
    if (!fromCookie || !userStore.data) return;
    if (this.pendingPostOnboardingRedirect) return; // don't clobber an existing target
    try {
      // Lazy import: a static import would create a module-eval cycle with
      // userSettings (which reads userStore.data at construction).
      const { setUserSetting } = await import('@lib/settings/userSettings');
      await setUserSetting(POST_ONBOARDING_REDIRECT_KEY, fromCookie);
    } catch (e) {
      console.warn('Failed to persist post-onboarding redirect:', e);
    }
  };

  /**
   * Honor (once) a remembered post-onboarding destination. The caller must
   * ensure the user is verified. Internal paths navigate in-app; allowed
   * external URLs get the auth token appended (same exchange-token handoff as
   * redirectIfRequested). The setting is cleared FIRST so a blocked/failed
   * target can't re-fire on every render. Returns true if a redirect was
   * performed.
   */
  honorPostOnboardingRedirect = async (): Promise<boolean> => {
    if (this.postOnboardingRedirectInFlight) return false;
    const dest = this.pendingPostOnboardingRedirect;
    if (!dest) return false;
    this.postOnboardingRedirectInFlight = true;

    try {
      // Lazy import to avoid a module-eval cycle with userSettings.
      const { removeUserSetting } = await import('@lib/settings/userSettings');
      await removeUserSetting(POST_ONBOARDING_REDIRECT_KEY);
    } catch (e) {
      console.warn('Failed to clear post-onboarding redirect; skipping:', e);
      this.postOnboardingRedirectInFlight = false;
      return false;
    }

    // The setting is now cleared, so pendingPostOnboardingRedirect is null and
    // the `!dest` guard above protects against re-entry. The in-flight flag
    // only needed to cover the async clear window; release it here so no
    // return path below (blocked external, blocked internal, or a successful
    // in-app navigate that doesn't reload) leaves the singleton stuck `true`.
    this.postOnboardingRedirectInFlight = false;

    if (isExternalUrl(dest)) {
      if (!isAllowedExternalRedirect(dest, ALLOWED_EXTERNAL_REDIRECT_PATTERNS)) {
        console.error('[postAuthRedirect] blocked external post-onboarding redirect:', dest);
        return false;
      }
      // Same event, different trigger: this destination comes from the post-signup
      // cookie the marketing site drops, so it's a genuine one-way exit.
      return this.navigateExternalWithToken(dest, 'post-onboarding-cookie');
    }

    // Internal: same-origin path only (mirrors the redirectAfterAuth validation).
    if (
      !dest.startsWith('/') ||
      dest.includes('//') ||
      dest.includes('..') ||
      dest.includes('\\')
    ) {
      console.error('[postAuthRedirect] blocked invalid internal post-onboarding redirect:', dest);
      return false;
    }
    router.navigate(dest);
    return true;
  };

  /**
   * Drop any remembered post-onboarding destination WITHOUT navigating. Called
   * when an explicit redirect (an external `?redirect=` or a stored internal
   * deep link) takes precedence, so the durable fallback can't later re-fire
   * to a stale target. No-op when nothing is pending.
   *
   * Best-effort: on an external redirect the page may unload before this
   * request settles. If so, the fallback simply fires once on the next plain
   * visit and clears itself then (see `honorPostOnboardingRedirect`) — it can
   * no longer hijack the explicit redirect, which already won.
   */
  clearPostOnboardingRedirect = async (): Promise<void> => {
    if (!this.pendingPostOnboardingRedirect) return;
    try {
      const { removeUserSetting } = await import('@lib/settings/userSettings');
      await removeUserSetting(POST_ONBOARDING_REDIRECT_KEY);
    } catch (e) {
      console.warn('Failed to clear post-onboarding redirect:', e);
    }
  };
}

// Singleton: constructed at module eval so the entry-URL `?redirect=` capture
// happens before any render or navigation can strip it.
export const postAuthRedirectStore = new PostAuthRedirectStore();

// @ts-ignore
window.postAuthRedirectStore = postAuthRedirectStore;
